import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scale, FileCheck, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageSEO } from '../components/SEO/PageSEO';
import Mixpanel from '../utils/mixpanel';

// FAQ Schema for Terms page
const termsPageFaqSchema = [
  {
    question: "How does LetsHost's booking process work?",
    answer: "After you submit your event details, our concierge team reviews your requirements and presents 2-3 curated venue matches within 24-48 hours. You choose your preferred venue, and we facilitate the introduction. The venue handles contracts and deposits directly."
  },
  {
    question: "What is the LetsHost booking fee?",
    answer: "We charge a small booking fee for our matching and concierge service. This fee is paid before we begin the venue matching process. Contact us for current pricing details."
  },
  {
    question: "Do venues handle deposits and payments directly?",
    answer: "Yes, venues handle their own deposits, contracts, and payment arrangements. LetsHost facilitates the introduction and provides guidance, but all financial transactions with venues occur directly between you and the venue."
  },
  {
    question: "What happens if a venue cancels my booking?",
    answer: "Venue cancellations are rare but can happen. LetsHost will work to find an alternative venue that meets your requirements. Any deposits paid directly to venues are subject to their individual cancellation policies."
  },
  {
    question: "Can I book ancillary services through LetsHost?",
    answer: "For certain services like photography, we may facilitate bookings through our platform. These providers operate independently under their own terms and conditions. Contact us to discuss your specific needs."
  }
];

const sections = [
  {
    id: 'introduction',
    title: 'Introduction',
    content: `Welcome to LetsHost! These Terms and Conditions ("Terms") govern your use of our venue booking platform and related services. By using our platform, you agree to be bound by these Terms. Please read them carefully before proceeding.`
  },
  {
    id: 'services',
    title: '2. Our Services',
    content: (
      <>
        <h3 className="text-base font-semibold mt-4 mb-2 text-stone-800">2.1 Venue Booking & Matching</h3>
        <p className="text-sm text-stone-500 mb-4">LetsHost connects event hosts with venues through a curated matching process.</p>

        <h3 className="text-base font-semibold mt-4 mb-2 text-stone-800">2.2 Concierge Support</h3>
        <p className="text-sm text-stone-500 mb-4">
          We charge a small booking fee before beginning the matchmaking process for our personalized venue matching service. This fee is subject to change. 
          We will provide you with a reasonable number of venue options (typically 3).
        </p>

        <h3 className="text-base font-semibold mt-4 mb-2 text-stone-800">2.3 Venue Terms</h3>
        <p className="text-sm text-stone-500">
          Each venue operates under its own terms and conditions. Venues typically handle their own deposits, contracts, and payment arrangements directly. 
          LetsHost does not collect venue deposits except in specific cases involving ancillary services (such as photography).
        </p>
      </>
    )
  },
  {
    id: 'booking',
    title: '3. Booking Process',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li><span className="font-semibold text-stone-700">Booking Fee:</span> A small booking fee is charged before beginning the matchmaking process (subject to change)</li>
        <li><span className="font-semibold text-stone-700">Venue Arrangements:</span> All venue-specific arrangements, including deposits and contracts, are handled directly by the venues</li>
        <li><span className="font-semibold text-stone-700">Ancillary Services:</span> In cases where LetsHost facilitates ancillary services (e.g., photography), deposits may be required and processed through our platform</li>
      </ul>
    )
  },
  {
    id: 'responsibilities',
    title: '4. User Responsibilities',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li><span className="font-semibold text-stone-700">Accurate Information:</span> Provide accurate and complete information for proper service delivery</li>
        <li><span className="font-semibold text-stone-700">Venue Compliance:</span> Adhere to venue-specific rules and requirements</li>
        <li><span className="font-semibold text-stone-700">Communication:</span> Maintain open communication regarding bookings and arrangements</li>
      </ul>
    )
  },
  {
    id: 'payment',
    title: '5. Payment Terms',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li><span className="font-semibold text-stone-700">Platform Payments:</span> Booking fee and any ancillary service payments processed via Stripe</li>
        <li><span className="font-semibold text-stone-700">Venue Payments:</span> Handled directly by venues unless otherwise specified</li>
      </ul>
    )
  },
  {
    id: 'fairuse',
    title: '6. Fair Use & Conduct',
    content: `Users must use the platform responsibly and legally. Activities compromising platform security or functionality are prohibited.`
  },
  {
    id: 'liability',
    title: '7. Limitation of Liability',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>Services provided "as is" without warranties</li>
        <li>Not liable for venue-related incidents or disputes</li>
        <li>Not responsible for third-party venue services or arrangements</li>
      </ul>
    )
  },
  {
    id: 'ip',
    title: '8. Intellectual Property',
    content: `All content and intellectual property on the LetsHost platform are owned by LetsHost or its licensors. Users receive a limited license to access and use the platform for its intended purpose.`
  },
  {
    id: 'changes',
    title: '9. Changes to Terms',
    content: `We may update these Terms to reflect changes in our services or legal requirements. Significant changes will be communicated via email or platform notices.`
  },
  {
    id: 'governing',
    title: '10. Governing Law',
    content: `These Terms are governed by New York State law. Any disputes will be resolved in New York state or federal courts.`
  },
  {
    id: 'ancillary',
    title: '11. Ancillary Services',
    content: `When booking ancillary services through our platform (such as photography), these providers are independent contractors. While we may facilitate payments for these services, each provider operates under their own terms and conditions.`
  },
  {
    id: 'contact',
    title: '12. Contact Us',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>Terms & Legal Inquiries: <a href="mailto:privacy@letshost.nyc?subject=Terms%20and%20Conditions%20Question" className="text-stone-700 underline hover:text-stone-900">privacy@letshost.nyc</a></li>
        <li>General Inquiries: <a href="mailto:events@letshost.nyc?subject=General%20Inquiry" className="text-stone-700 underline hover:text-stone-900">events@letshost.nyc</a></li>
      </ul>
    )
  }
];

export default function Terms() {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (id) => {
    const isOpening = !openSections[id];
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
    
    if (isOpening) {
      Mixpanel.track('Terms Section Opened', { 
        section: id,
        sectionTitle: sections.find(section => section.id === id)?.title || id
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSEO
        title="Terms of Service | LetsHost NYC"
        description="LetsHost's terms and conditions for NYC venue booking services. Learn about our booking process, fees, and policies for private dining and corporate event arrangements."
        path="/terms"
        faqSchema={termsPageFaqSchema}
      />
      <Navbar />
      <main className="flex-grow">
        {/* ───────── HERO ───────── */}
        <section className="pb-8 sm:pb-12 px-5 sm:px-10 lg:px-16" style={{ backgroundColor: '#F5F0EB' }}>
          <div className="max-w-7xl mx-auto mt-2 sm:mt-6 md:mt-8">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <span className="text-xs tracking-[0.16em] uppercase text-stone-400 font-sans font-medium block mb-5">
                Legal
              </span>
              <h1
                className="text-[2.75rem] sm:text-[3.5rem] md:text-[4.2rem] font-bold text-gray-900 leading-[1.06] mb-4 max-w-2xl"
                style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
              >
                Terms & Conditions
              </h1>
              <p className="text-base sm:text-lg text-stone-500 leading-relaxed mb-8 max-w-md">
                Last updated: {new Date().toLocaleDateString()}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                {[
                  { icon: Scale, label: 'Fair Use' },
                  { icon: FileCheck, label: 'Compliance' },
                  { icon: AlertCircle, label: 'Liability' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-stone-200/60">
                    <div className="flex items-center gap-3">
                      <div className="bg-stone-100 p-2 rounded-lg">
                        <Icon className="w-5 h-5 text-stone-500" />
                      </div>
                      <h3
                        className="text-base font-semibold text-stone-800"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {label}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ───────── CONTENT ───────── */}
        <section className="max-w-4xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
          <div className="divide-y divide-stone-200">
            {sections.map(section => (
              <div key={section.id} id={section.id}>
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between py-5 text-left hover:bg-stone-50 rounded-lg px-3 transition-colors"
                >
                  <h2
                    className="text-lg font-semibold text-stone-800"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {section.title}
                  </h2>
                  {openSections[section.id] ? (
                    <ChevronUp className="w-5 h-5 text-stone-400 shrink-0 ml-4" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-stone-400 shrink-0 ml-4" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openSections[section.id] ? 'max-h-[2000px] opacity-100 mb-4' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pb-4 px-3">
                    {typeof section.content === 'string' ? (
                      <p className="text-stone-500 text-sm leading-relaxed">{section.content}</p>
                    ) : (
                      section.content
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
